import React, { useEffect } from 'react';
import { Bell, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { useAlertStore } from '../store';
import { alertService } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const AlertsPanel = () => {
  const { alerts, setAlerts, markAsRead, removeAlert } = useAlertStore();
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await alertService.getAll({ limit: 10 });
      setAlerts(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await alertService.markAsRead(alertId);
      markAsRead(alertId);
    } catch (error) {
      console.error('Erro ao marcar alerta:', error);
      toast.error('Erro ao marcar alerta como lido');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await alertService.delete(alertId);
      removeAlert(alertId);
      toast.success('Alerta removido');
    } catch (error) {
      console.error('Erro ao deletar alerta:', error);
      toast.error('Erro ao remover alerta');
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'speed_limit':
      case 'geofence_exit':
      case 'sos':
        return <AlertTriangle className="w-5 h-5 text-danger-400" />;
      case 'geofence_enter':
      case 'ignition_on':
        return <Bell className="w-5 h-5 text-primary-400" />;
      case 'ignition_off':
        return <CheckCircle className="w-5 h-5 text-success-400" />;
      default:
        return <Bell className="w-5 h-5 text-warning-400" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'border-l-4 border-danger-500';
      case 'high':
        return 'border-l-4 border-warning-500';
      case 'medium':
        return 'border-l-4 border-primary-500';
      default:
        return 'border-l-4 border-dark-600';
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">Alertas Recentes</h2>
        <Bell className="w-5 h-5 text-dark-400" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-8 text-dark-400">
          <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Nenhum alerta</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`
                p-4 rounded-lg bg-dark-700 transition-all duration-200
                ${!alert.is_read ? 'bg-opacity-100' : 'bg-opacity-50'}
                ${getSeverityColor(alert.severity)}
              `}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-1">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">
                      {alert.title}
                    </h4>
                    <p className="text-sm text-dark-300 mb-2">
                      {alert.message}
                    </p>
                    {alert.vehicle && (
                      <p className="text-xs text-dark-400">
                        Veículo: {alert.vehicle.name} ({alert.vehicle.plate})
                      </p>
                    )}
                    <p className="text-xs text-dark-500 mt-1">
                      {format(new Date(alert.created_at), 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>
                </div>

                <div className="flex gap-1">
                  {!alert.is_read && (
                    <button
                      onClick={() => handleMarkAsRead(alert.id)}
                      className="p-1 rounded hover:bg-dark-600 transition-colors"
                      title="Marcar como lido"
                    >
                      <CheckCircle className="w-4 h-4 text-success-400" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteAlert(alert.id)}
                    className="p-1 rounded hover:bg-dark-600 transition-colors"
                    title="Remover"
                  >
                    <X className="w-4 h-4 text-danger-400" />
                  </button>
                </div>
              </div>

              {alert.address && (
                <div className="mt-2 pt-2 border-t border-dark-600">
                  <p className="text-xs text-dark-400">
                    📍 {alert.address}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {alerts.length > 0 && (
        <div className="mt-4 pt-4 border-t border-dark-700">
          <a
            href="/alerts"
            className="text-sm text-primary-400 hover:text-primary-300 font-medium"
          >
            Ver todos os alertas →
          </a>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;
